import React, { useCallback, useEffect, useState } from "react";
import api, { ApiResponse } from "../services/api";
import { useOrder } from "./EmpOrderContext";
import { toast } from "react-hot-toast";
import { activeCurrencySymbol } from "../utils/currency";

interface TempOrderItem {
  itemId: string;
  name: string;
  kitchenName?: string;
  quantity: number;
  price: number;
  customOptions?: { label: string; price: number; qty: number }[];
}

interface TempOrder {
  _id: string;
  tokenNumber: number;
  items: TempOrderItem[];
  totalAmount: number;
  discountAmount?: number;
  choice?: string;
  tableToken?: number;
  phoneNumber?: string;
  employeeName?: string;
  createdAt: string;
}

const ApproveOrders: React.FC = () => {
  const { user } = useOrder();
  const [tempOrders, setTempOrders] = useState<TempOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<TempOrder | null>(null);
  const [approving, setApproving] = useState(false);

  const fetchTempOrders = useCallback(async () => {
    if (!user?.restaurantId) return;
    try {
      setLoading(true);
      const res = await api.get<ApiResponse<TempOrder[]>>(
        `/temp-orders?restaurantId=${user.restaurantId}`
      );
      if (res.data.success && Array.isArray(res.data.data)) {
        setTempOrders(res.data.data);
      }
    } catch {
      setTempOrders([]);
    } finally {
      setLoading(false);
    }
  }, [user?.restaurantId]);

  useEffect(() => {
    fetchTempOrders();
    const interval = setInterval(fetchTempOrders, 15000);
    return () => clearInterval(interval);
  }, [fetchTempOrders]);

  const handleApprove = async (payChoice: "cash" | "card" | "qr") => {
    if (!selectedOrder) return;
    setApproving(true);
    try {
      const res = await api.post<ApiResponse<any>>(
        `/temp-orders/${selectedOrder._id}/approve`,
        { payChoice }
      );
      if (res.data.success) {
        toast.success(`Order #${String(selectedOrder.tokenNumber).padStart(3, "0")} approved!`);
        setSelectedOrder(null);
        fetchTempOrders();
      } else {
        toast.error("Failed to approve order");
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to approve order");
    } finally {
      setApproving(false);
    }
  };

  const getItemTotal = (item: TempOrderItem) => {
    const optionsTotal = (item.customOptions || []).reduce(
      (s, o) => s + o.price * o.qty,
      0
    );
    return (item.price + optionsTotal) * item.quantity;
  };

  if (loading) {
    return (
      <div className="px-6 pt-4 text-gray-500">Loading pending tokens...</div>
    );
  }

  if (tempOrders.length === 0) {
    return (
      <div className="px-6 pt-4 text-gray-500">No pending counter orders.</div>
    );
  }

  return (
    <div className="px-6 pb-8">
      <div className="flex flex-wrap gap-4 mt-2">
        {tempOrders.map((order) => (
          <div
            key={order._id}
            onClick={() => setSelectedOrder(order)}
            className="w-64 border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white cursor-pointer hover:shadow-md transition-shadow"
          >
            <div
              className="p-3 text-white font-semibold"
              style={{ backgroundColor: "#113E60" }}
            >
              <div className="flex justify-between items-center">
                <span className="text-xs font-outfit text-white/80">Token</span>
                <span className="text-3xl font-extrabold font-outfit">
                  {String(order.tokenNumber).padStart(3, "0")}
                </span>
              </div>
              <div className="text-xs font-outfit text-white/70 mt-1 capitalize">
                {order.choice || "counter"}{" "}
                {order.tableToken ? `· Table ${order.tableToken}` : ""}
              </div>
            </div>

            <div className="px-3 pt-2 pb-1 text-xs text-gray-400 flex justify-between">
              <span>Item</span>
              <span>Total</span>
            </div>
            <div className="overflow-y-auto max-h-36 no-scrollbar">
              {order.items.map((item, i) => (
                <div
                  key={i}
                  className="px-3 py-1.5 border-b border-gray-100 text-sm flex justify-between"
                >
                  <span className="text-[#113E60] font-outfit">
                    {item.quantity}x {item.kitchenName || item.name}
                  </span>
                  <span className="text-[#113E60] font-outfit">
                    {activeCurrencySymbol()}{getItemTotal(item).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
            <div className="px-3 py-2 flex justify-between text-sm font-semibold text-[#113E60]">
              <span>Total</span>
              <span>{activeCurrencySymbol()}{Number(order.totalAmount).toFixed(2)}</span>
            </div>
            <div className="px-3 pb-2 text-xs text-gray-400">
              {new Date(order.createdAt).toLocaleTimeString()}
            </div>
          </div>
        ))}
      </div>

      {/* Payment Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
            <div
              className="p-4 text-white"
              style={{ backgroundColor: "#113E60" }}
            >
              <div className="flex justify-between items-center">
                <span className="font-semibold text-lg">
                  Token{" "}
                  {String(selectedOrder.tokenNumber).padStart(3, "0")}
                </span>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="text-white/70 hover:text-white text-xl font-bold"
                >
                  ×
                </button>
              </div>
              <div className="text-sm text-white/70 capitalize mt-0.5">
                {selectedOrder.choice || "counter"}
              </div>
            </div>

            <div className="p-4">
              <div className="text-xs text-gray-400 flex justify-between mb-1">
                <span>Items</span>
                <span>Qty · Total</span>
              </div>
              <div className="overflow-y-auto max-h-48 no-scrollbar divide-y divide-gray-100">
                {selectedOrder.items.map((item, i) => (
                  <div key={i} className="py-2 flex justify-between text-sm">
                    <span className="text-[#113E60]">
                      {item.kitchenName || item.name}
                    </span>
                    <span className="text-[#113E60]">
                      {item.quantity} · {activeCurrencySymbol()}{getItemTotal(item).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex justify-between font-semibold text-[#113E60] mt-3 pt-2 border-t border-gray-100">
                <span>Total</span>
                <span>{activeCurrencySymbol()}{Number(selectedOrder.totalAmount).toFixed(2)}</span>
              </div>

              {String((selectedOrder as any).payChoice || "").toLowerCase().includes("qr") && (
                <p className="mt-4 rounded-lg bg-yellow-50 px-3 py-2 text-center text-sm text-yellow-800">
                  Customer says they paid by QR — check the payment in your UPI/bank app before approving.
                </p>
              )}
              <p className="text-center text-sm text-gray-500 mt-4 mb-3">
                Select payment method
              </p>
              <div className="flex gap-3">
                <button
                  disabled={approving}
                  onClick={() => handleApprove("cash")}
                  className="flex-1 py-3 rounded-xl bg-green-600 text-white font-semibold text-sm hover:bg-green-700 disabled:opacity-50 transition"
                >
                  Cash
                </button>
                <button
                  disabled={approving}
                  onClick={() => handleApprove("card")}
                  className="flex-1 py-3 rounded-xl bg-main text-white font-semibold text-sm hover:bg-main/90 disabled:opacity-50 transition"
                >
                  Card
                </button>
                <button
                  disabled={approving}
                  onClick={() => handleApprove("qr")}
                  className="flex-1 py-3 rounded-xl bg-purple-600 text-white font-semibold text-sm hover:bg-purple-700 disabled:opacity-50 transition"
                >
                  QR
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApproveOrders;
